import {useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import dayjs from 'dayjs';
import styled from 'styled-components';
import {useQueries, useQuery, UseQueryResult} from 'react-query';
import {useLocation, useNavigate, useParams} from 'react-router';
import PageLayoutTemplate from '@ui/templates/page-template/page-layout';
import {Avatar} from '@ui/atoms/avatar';
import {Body1, Body2} from '@ui/atoms/typography';
import {CustomMarkdown} from '@ui/atoms/typography/markdown';
import {markdownToPlainText} from '@ui/molecules/editor/rich-text.utils';
import {Button} from '@ui/atoms/button';
import {ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon} from '@ui/atoms/icons';
import {ItemLoader} from '@ui/organisms/item-loader';
import {useStoreContext} from '@store/store-context';
import {useFirebaseFetch} from '@hooks/query-hook';
import {getPlural} from '@utils/get-plural';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {
  ICheckinCycleDetail,
  ISpecificCheckinsWithResponse,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {canAdministerCheckins} from '../checkins-access';
import {
  TeamFeedAnswer,
  buildTeamFeedAnswers,
} from '../my_checkins/team-feed.utils';
import {TeamFeedComments} from '../my_checkins/components/team-feed-comments';
import {GoalReflectionResponse} from '../my_checkins/components/goal-reflection-response';
import {
  attachCycleEnrichment,
  buildOccurrenceGroups,
  checkinSubmissionStatusLabel,
  getAdminSubmissionFilter,
  getSubmissionRows,
  ManagedCycle,
  withoutPendingInvitationSubmissions,
} from './team-management.utils';
import {
  ParticipantRange,
  ParticipantSend,
  buildParticipantCsv,
  buildParticipantIds,
  buildParticipantTimeline,
  getParticipant,
  getParticipantResponseSummary,
  selectParticipantSends,
} from './participant-checkin-detail.utils';

const ParticipantHeader = styled.section`
  display: grid;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 24px;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 24px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 16px;
  }
`;

const ParticipantIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 24px;
`;

const ParticipantActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  white-space: nowrap;
`;

const ParticipantArrowButton = styled.button`
  display: flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 8px;
  background: ${({theme}) => theme.colors.backgroundLight};

  svg path {
    stroke: ${({theme}) => theme.colors.textBody};
  }

  &:disabled {
    opacity: 0.5;
  }

  &:disabled svg path {
    stroke: ${({theme}) => theme.colors.textMuted};
  }
`;

const ParticipantResponseCard = styled.article`
  overflow: visible;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
`;

const getResponseData = (
  response?:
    | ISpecificCheckinsWithResponse
    | ISpecificCheckinsWithResponseData
    | false
    | null,
) => {
  if (!response) return null;
  return 'data' in response ? response.data : response;
};

const participantName = (user?: {
  firstName?: string;
  lastName?: string;
  email?: string;
}) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
  user?.email ||
  'Participant';

const AnswerBlock = ({answer}: {answer: TeamFeedAnswer}) => {
  if (answer.kind === 'pulse') {
    return (
      <div className="flex flex-col gap-2">
        <Body2 kind="textBody" weight="semibold">
          {answer.prompt}
        </Body2>
        <div className="flex items-center gap-3">
          <span role="img" aria-label={answer.label} className="text-[32px]">
            {answer.emoji}
          </span>
          <Body2 kind="textBody" weight="semibold">
            {answer.label}
          </Body2>
        </div>
        {answer.feedback && <Body2 kind="textDark">{answer.feedback}</Body2>}
      </div>
    );
  }

  if (answer.kind === 'checklist') {
    return (
      <div className="flex flex-col gap-2">
        <Body2 kind="textBody" weight="semibold">
          {answer.prompt}
        </Body2>
        {answer.items.map((item, index) => (
          <div
            className="flex items-center gap-2"
            key={`${answer.id}-${index}`}>
            <input type="checkbox" checked={item.completed} readOnly />
            <Body2 kind="textDark">{item.text}</Body2>
          </div>
        ))}
      </div>
    );
  }

  if (answer.kind === 'goals') {
    return (
      <div className="flex flex-col gap-3">
        <Body2 kind="textBody" weight="semibold">
          {answer.prompt}
        </Body2>
        <GoalReflectionResponse response={answer.response} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Body2 kind="textBody" weight="semibold">
        {answer.prompt}
      </Body2>
      <CustomMarkdown
        text={answer.response}
        compact
        className="text-[14px] leading-[22px] text-blackTwo"
      />
    </div>
  );
};

const answerPreview = (answer?: TeamFeedAnswer) => {
  if (!answer) return 'Submission received';
  if (answer.kind === 'pulse') return `${answer.emoji} ${answer.label}`;
  if (answer.kind === 'checklist')
    return answer.items.map((item) => item.text).join(', ');
  return markdownToPlainText(answer.response);
};

const RangeButton = ({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex whitespace-nowrap items-center justify-center gap-2 rounded-[20px] border px-[14px] py-1 text-sm font-medium leading-[22px] ${
      active
        ? 'border-purple300 bg-purple300 text-alwaysWhite'
        : 'border-textMuted bg-transparent text-textBody'
    }`}>
    {children}
  </button>
);

const EmptySend = ({send}: {send: ParticipantSend}) => (
  <div
    className="flex items-center gap-3 py-2"
    data-testid={`empty-send-${send.key}`}>
    <div className="h-px flex-1 bg-borderLight" />
    <span className="rounded-md bg-backgroundDark px-2 py-1 text-xs text-textMuted">
      {checkinSubmissionStatusLabel({
        kind: send.status,
        daysOverdue: send.daysOverdue,
      })}{' '}
      ·{' '}
      {dayjs(send.key).isValid() ? dayjs(send.key).format('MMM D') : send.label}
    </span>
    <div className="h-px flex-1 bg-borderLight" />
  </div>
);

const ParticipantCommentCount = ({
  submissionId,
  participantId,
}: {
  submissionId: string;
  participantId: string;
}) => {
  const {data: comments = {}} = useFirebaseFetch(
    `checkins/comments/${submissionId}/${participantId}/comments`,
  );
  const count = Object.keys(comments || {}).length;
  return (
    <span className="text-sm text-textBody">
      {count} {count === 1 ? 'comment' : 'comments'}
    </span>
  );
};

export const ParticipantCheckinDetail = observer(() => {
  const {cycleId, participantId} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    authStore: {auth},
    usersStore: {allUsers},
  } = useStoreContext();
  const canManageCycles = canAdministerCheckins(auth.user.role);
  const workspaceId = auth.user.workspace.id;
  const request = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const controller = useMemo(() => new CompanyCheckinsController(request), [
    request,
  ]);
  const baseFilter = getAdminSubmissionFilter(cycleId);
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');
  const requestedSendKey = searchParams.get('send');

  const {data: primaryResponse, isLoading: loadingPrimary} = useQuery(
    ['cycle-submissions', workspaceId, cycleId, 'user'],
    () => controller.getListOfCheckins({...baseFilter, include: 'user'}),
    {enabled: canManageCycles && Boolean(cycleId), refetchOnWindowFocus: false},
  );
  const {data: enrichmentResponse, isLoading: loadingEnrichment} = useQuery(
    ['cycle-submissions', workspaceId, cycleId, 'cycle'],
    () => controller.getListOfCheckins({...baseFilter, include: 'cycle'}),
    {enabled: canManageCycles && Boolean(cycleId), refetchOnWindowFocus: false},
  );
  const {data: cycleResponse, isLoading: loadingCycle} = useQuery<
    ICheckinCycleDetail | false
  >(
    ['check-in-cycle', cycleId],
    () => controller.getCheckinCycle(cycleId as string),
    {enabled: canManageCycles && Boolean(cycleId), refetchOnWindowFocus: false},
  );

  const submissions = useMemo(
    () =>
      withoutPendingInvitationSubmissions(
        attachCycleEnrichment(
          getSubmissionRows(primaryResponse),
          getSubmissionRows(enrichmentResponse),
        ).filter((submission) => {
          const submissionCycle =
            typeof submission.cycle === 'object'
              ? submission.cycle.id
              : submission.cycle || submission.cycleId;
          return submissionCycle === cycleId;
        }),
        allUsers,
      ),
    [allUsers, cycleId, enrichmentResponse, primaryResponse],
  );
  const cycle = cycleResponse || undefined;
  const occurrences = useMemo(
    () =>
      cycle
        ? buildOccurrenceGroups(
            submissions,
            cycle as ManagedCycle,
            auth.user.workspace?.timezone,
          )
        : [],
    [auth.user.workspace?.timezone, cycle, submissions],
  );
  const participantIds = useMemo(() => buildParticipantIds(occurrences), [
    occurrences,
  ]);
  const timeline = useMemo(
    () => buildParticipantTimeline(occurrences, participantId),
    [occurrences, participantId],
  );
  const participant = useMemo(
    () => getParticipant(submissions, participantId),
    [participantId, submissions],
  );
  const responseSummary = useMemo(
    () => getParticipantResponseSummary(timeline),
    [timeline],
  );
  const [range, setRange] = useState<ParticipantRange>(
    requestedSendKey ? 'custom' : 'latest',
  );
  const [customKeys, setCustomKeys] = useState<string[]>(
    requestedSendKey ? [requestedSendKey] : [],
  );
  const [draftCustomKeys, setDraftCustomKeys] = useState<string[]>([]);
  const [customOpen, setCustomOpen] = useState(false);
  const appliedRequestedSend = useRef<string | null>(null);
  const selectedSends = useMemo(
    () => selectParticipantSends(timeline, range, customKeys),
    [customKeys, range, timeline],
  );
  const selectedSubmitted = useMemo(
    () =>
      selectedSends.filter(
        (send) => send.status === 'submitted' && send.submission?.id,
      ),
    [selectedSends],
  );
  const detailQueries = useQueries(
    selectedSubmitted.map((send) => ({
      queryKey: [
        'cycle-participant-response',
        workspaceId,
        cycleId,
        send.submission?.id,
      ],
      queryFn: () =>
        controller.getCheckinByIdAndFetchResponses(send.submission?.id || ''),
      enabled: Boolean(send.submission?.id),
      refetchOnWindowFocus: false,
      retry: false,
    })),
  ) as UseQueryResult<ISpecificCheckinsWithResponse | false>[];
  const detailsById = useMemo(() => {
    const details: Record<
      string,
      ISpecificCheckinsWithResponseData | null
    > = {};
    selectedSubmitted.forEach((send, index) => {
      const id = send.submission?.id;
      if (id) details[id] = getResponseData(detailQueries[index]?.data);
    });
    return details;
  }, [detailQueries, selectedSubmitted]);
  const answersById = useMemo(() => {
    const answers: Record<string, TeamFeedAnswer[]> = {};
    Object.entries(detailsById).forEach(([id, detail]) => {
      answers[id] = buildTeamFeedAnswers(detail, cycle || null);
    });
    return answers;
  }, [cycle, detailsById]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const initialExpandedKey =
    selectedSends.find((send) => send.status === 'submitted')?.key || null;

  useEffect(() => {
    setExpandedKey(initialExpandedKey);
  }, [customKeys, initialExpandedKey, participantId, range]);

  useEffect(() => {
    const requestedSelection = `${participantId || ''}:${
      requestedSendKey || 'latest'
    }`;
    if (appliedRequestedSend.current === requestedSelection) return;
    if (requestedSendKey && !timeline.length) return;

    appliedRequestedSend.current = requestedSelection;
    if (!requestedSendKey) {
      setRange('latest');
      setCustomKeys([]);
      setDraftCustomKeys([]);
      return;
    }

    const requestedSend = timeline.find(
      (send) => send.key === requestedSendKey && send.status === 'submitted',
    );
    if (requestedSend) {
      setRange('custom');
      setCustomKeys([requestedSend.key]);
      setDraftCustomKeys([requestedSend.key]);
      return;
    }
    setRange('latest');
    setCustomKeys([]);
    setDraftCustomKeys([]);
  }, [participantId, requestedSendKey, timeline]);

  useEffect(() => {
    if (!canManageCycles) navigate('/checkins/my-checkins', {replace: true});
  }, [canManageCycles, navigate]);

  useEffect(() => {
    if (cycle && cycle.visibility === 'anonymous') {
      navigate(`/checkins/team-management/cycles/${cycleId}?tab=responses`, {
        replace: true,
      });
    }
  }, [cycle, cycleId, navigate]);

  const currentIndex = participantIds.indexOf(participantId || '');
  const navigateParticipant = (nextIndex: number) => {
    const nextId = participantIds[nextIndex];
    if (!nextId || !cycleId) return;
    const params = new URLSearchParams();
    if (returnTo) params.set('returnTo', returnTo);
    if (requestedSendKey) params.set('send', requestedSendKey);
    navigate(
      `/checkins/team-management/cycles/${cycleId}/participants/${nextId}${
        params.toString() ? `?${params.toString()}` : ''
      }`,
    );
  };

  const setPreset = (nextRange: ParticipantRange) => {
    setCustomOpen(false);
    setRange(nextRange);
  };

  const openCustom = () => {
    setDraftCustomKeys(
      customKeys.length
        ? customKeys
        : timeline
            .filter((send) => send.status === 'submitted')
            .slice(0, 3)
            .map((send) => send.key),
    );
    setCustomOpen(true);
  };

  const downloadCsv = () => {
    if (!cycle || !participant) return;
    const csv = buildParticipantCsv(
      participantName(participant),
      cycle,
      selectedSends,
      answersById,
    );
    const blob = new Blob([`\ufeff${csv}`], {type: 'text/csv;charset=utf-8'});
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${cycle.cycleName}-${participantName(participant)}.csv`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const isLoading = loadingPrimary || loadingEnrichment || loadingCycle;
  const detailsLoading = detailQueries.some((query) => query.isLoading);
  const mentionableUserIds = participantIds.filter((id) => id !== auth.user.id);
  const closeTarget =
    returnTo || `/checkins/team-management/cycles/${cycleId}?tab=responses`;

  return (
    <PageLayoutTemplate
      title={cycle?.cycleName || 'Check-in submissions'}
      onClick={() => navigate(closeTarget)}
      bodyStyle={{backgroundColor: '#FAFAFA'}}>
      <main className="mx-auto flex w-full max-w-[880px] flex-col gap-6 pb-12">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <ItemLoader />
          </div>
        ) : !cycle || !participant || currentIndex < 0 ? (
          <section className="rounded-[10px] border border-borderLight bg-white p-6 text-center">
            <Body1 kind="textDark" weight="semibold">
              Participant submissions could not be found.
            </Body1>
            <div className="mt-4">
              <Button kind="secondary" onClick={() => navigate(closeTarget)}>
                Return to cycle responses
              </Button>
            </div>
          </section>
        ) : (
          <>
            <ParticipantHeader data-testid="participant-header">
              <ParticipantIdentity>
                <ParticipantArrowButton
                  type="button"
                  aria-label="Previous participant"
                  disabled={currentIndex <= 0}
                  onClick={() => navigateParticipant(currentIndex - 1)}>
                  <ArrowLeftIcon />
                </ParticipantArrowButton>
                <Avatar
                  tooltip={true}
                  userId={participant.id || participantId || ''}
                  name={participantName(participant)}
                  size="lg"
                  timezone={participant.timezone}
                  src={participant.avatar?.url}
                />
                <div className="min-w-0">
                  <Body1 kind="textDark" weight="semibold">
                    {participantName(participant)}
                  </Body1>
                  <Body2 kind="textBody">
                    {participant.jobTitle || 'Team member'} · Responded to{' '}
                    {responseSummary.submitted} of {responseSummary.total}{' '}
                    {getPlural('send', responseSummary.total)}
                  </Body2>
                </div>
              </ParticipantIdentity>
              <ParticipantActions>
                <ParticipantArrowButton
                  type="button"
                  aria-label="Next participant"
                  disabled={currentIndex >= participantIds.length - 1}
                  onClick={() => navigateParticipant(currentIndex + 1)}>
                  <ArrowRightIcon />
                </ParticipantArrowButton>
                <Button
                  kind="secondary"
                  onClick={downloadCsv}
                  disabled={!selectedSends.length || detailsLoading}
                  style={{height: 38, padding: '8px 16px', borderRadius: 8}}>
                  {detailsLoading
                    ? 'Preparing CSV…'
                    : `Export CSV (${selectedSends.length} ${getPlural(
                        'send',
                        selectedSends.length,
                      )})`}
                </Button>
              </ParticipantActions>
            </ParticipantHeader>

            <section className="relative flex flex-wrap items-center gap-2">
              <Body2 kind="textBody" weight="semibold" className="mr-2">
                Showing:
              </Body2>
              <RangeButton
                active={range === 'latest'}
                onClick={() => setPreset('latest')}>
                Latest
              </RangeButton>
              <RangeButton
                active={range === 'last-3'}
                onClick={() => setPreset('last-3')}>
                Last 3 sends
              </RangeButton>
              <RangeButton
                active={range === 'last-6'}
                onClick={() => setPreset('last-6')}>
                Last 6
              </RangeButton>
              <RangeButton active={range === 'custom'} onClick={openCustom}>
                <span>Custom</span>
                <span data-testid="custom-range-chevron" aria-hidden="true">
                  <ChevronDownIcon color="currentColor" />
                </span>
              </RangeButton>

              {customOpen && (
                <div
                  data-testid="custom-send-picker"
                  className="absolute left-0 top-[38px] z-30 w-[456px] max-w-[calc(100vw-32px)] overflow-hidden rounded-[7px] border border-borderDark bg-white shadow-[0_2px_5px_rgba(172,172,190,0.24)]">
                  <div className="flex h-12 items-center justify-between px-4">
                    <Body2 kind="textBody" weight="semibold">
                      Select sends
                    </Body2>
                    <div className="flex gap-4 text-sm font-medium">
                      <button
                        type="button"
                        className="text-purple300"
                        onClick={() =>
                          setDraftCustomKeys(
                            timeline
                              .filter((send) => send.status === 'submitted')
                              .map((send) => send.key),
                          )
                        }>
                        Select all
                      </button>
                      <button
                        type="button"
                        disabled={!draftCustomKeys.length}
                        className="text-textMuted disabled:opacity-50"
                        onClick={() => setDraftCustomKeys([])}>
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[190px] overflow-y-auto">
                    {timeline.map((send) => {
                      const selectable = send.status === 'submitted';
                      const checked = draftCustomKeys.includes(send.key);
                      return (
                        <label
                          key={send.key}
                          className={`flex h-[38px] items-center justify-between px-4 ${
                            checked ? 'bg-backgroundDark' : 'bg-white'
                          } ${
                            selectable ? 'cursor-pointer' : 'text-textMuted'
                          }`}>
                          <span className="flex items-center gap-6">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!selectable}
                              onChange={() =>
                                setDraftCustomKeys((current) =>
                                  current.includes(send.key)
                                    ? current.filter((key) => key !== send.key)
                                    : [...current, send.key],
                                )
                              }
                            />
                            <Body2 kind={selectable ? 'textDark' : 'textMuted'}>
                              {send.label}
                            </Body2>
                          </span>
                          <Body2 kind={selectable ? 'textDark' : 'textMuted'}>
                            {checkinSubmissionStatusLabel({
                              kind: send.status,
                              daysOverdue: send.daysOverdue,
                            })}
                          </Body2>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex h-[58px] items-center justify-end gap-4 bg-backgroundDark px-4">
                    <button
                      type="button"
                      onClick={() => setCustomOpen(false)}
                      className="text-xs font-semibold text-blackTwo">
                      Cancel
                    </button>
                    <Button
                      disabled={!draftCustomKeys.length}
                      onClick={() => {
                        setCustomKeys(draftCustomKeys);
                        setRange('custom');
                        setCustomOpen(false);
                      }}
                      style={{padding: '8px 16px'}}>
                      Apply
                    </Button>
                  </div>
                </div>
              )}
            </section>

            <section className="flex flex-col gap-3">
              {!selectedSends.length ? (
                <div className="rounded-[10px] border border-borderLight bg-white p-8 text-center">
                  <Body1 kind="textDark" weight="semibold">
                    No sends selected
                  </Body1>
                  <Body2 kind="textBody">
                    Choose at least one submitted send.
                  </Body2>
                </div>
              ) : (
                selectedSends.map((send, index) => {
                  if (send.status !== 'submitted' || !send.submission?.id)
                    return <EmptySend key={send.key} send={send} />;
                  const submissionId = send.submission.id;
                  const answers = answersById[submissionId] || [];
                  const query =
                    detailQueries[
                      selectedSubmitted.findIndex(
                        (item) => item.key === send.key,
                      )
                    ];
                  const expanded = expandedKey === send.key;
                  const latest = index === 0 && timeline[0]?.key === send.key;
                  return (
                    <ParticipantResponseCard
                      key={send.key}
                      data-testid={`participant-response-card-${send.key}`}>
                      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-borderLight px-6 py-4">
                        <Body2 kind="textDark" weight="semibold">
                          {send.label}
                          {latest ? ' (Latest)' : ''}
                        </Body2>
                        <div className="flex items-center gap-4">
                          <span className="rounded-lg bg-backgroundDark px-2 py-1 text-sm font-medium text-textBody">
                            Submitted{' '}
                            {dayjs(
                              send.submission.submittedAt ||
                                send.submission.updatedAt,
                            ).format('MMM D, h:mm A')}
                          </span>
                        </div>
                      </header>
                      {expanded ? (
                        <>
                          <div className="flex flex-col gap-6 p-6">
                            {query?.isLoading ? (
                              <ItemLoader />
                            ) : query?.isError ? (
                              <div className="flex items-center justify-between gap-4">
                                <Body2 kind="textBody">
                                  Unable to load this response.
                                </Body2>
                                <Button
                                  kind="secondary"
                                  onClick={() => query.refetch()}
                                  style={{padding: '6px 12px'}}>
                                  Retry
                                </Button>
                              </div>
                            ) : answers.length ? (
                              answers.map((answer) => (
                                <AnswerBlock
                                  key={answer.id}
                                  answer={answer}
                                />
                              ))
                            ) : (
                              <Body2 kind="textBody">
                                No response content was captured.
                              </Body2>
                            )}
                            <button
                              type="button"
                              onClick={() => setExpandedKey(null)}
                              className="self-start text-sm font-medium text-purple300">
                              − Collapse
                            </button>
                          </div>
                          <TeamFeedComments
                            submissionId={submissionId}
                            userId={participantId || ''}
                            mentionableUserIds={mentionableUserIds}
                            notificationContext={{
                              cycleId,
                              cycleName: cycle?.cycleName,
                              submissionId,
                              participantId,
                              ownerHref: `/checkins/history?cycleId=${encodeURIComponent(
                                cycleId || '',
                              )}&send=${encodeURIComponent(
                                send.key,
                              )}&comments=${encodeURIComponent(submissionId)}`,
                              defaultHref: `${location.pathname}${location.search}`,
                            }}
                          />
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setExpandedKey(send.key)}
                          className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
                          <Body2
                            kind="textBody"
                            className="min-w-0 flex-1 truncate">
                            “{answerPreview(answers[0])}”
                          </Body2>
                          <span className="flex shrink-0 items-center gap-6">
                            <ParticipantCommentCount
                              submissionId={submissionId}
                              participantId={participantId || ''}
                            />
                            <span className="rounded-lg border border-borderDark px-4 py-2 text-xs font-semibold text-blackTwo">
                              Expand
                            </span>
                          </span>
                        </button>
                      )}
                    </ParticipantResponseCard>
                  );
                })
              )}
            </section>
          </>
        )}
      </main>
    </PageLayoutTemplate>
  );
});

ParticipantCheckinDetail.displayName = 'ParticipantCheckinDetail';
